import React from "react";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";

import { TextLink } from "../TextLink";
import {
  ComponentNavigationLinkList,
  Enum_Componentnavigationlink_Imagefit,
  Enum_Componentnavigationlinklist_Align,
  Enum_Componentnavigationlinklist_Orientation,
} from "../../../graphql/generated/graphql";
import { notEmpty } from "../../../utils/notEmpty";
import { Paragraph } from "../../typography";
import { ILinkListComponentConfiguration } from "./types";

export function LinkList({
  links,
  orientation,
  textSize,
  label,
  externalUrl,
  page,
  linkTarget,
  align,
  gap,
  configuration,
}: ComponentNavigationLinkList & {
  configuration?: ILinkListComponentConfiguration;
}): JSX.Element {
  const textSizeClass = textSize?.data?.attributes?.value || "base";

  return (
    <div className="component-link-list">
      {label && (
        <div className="mb-2">
          <TextLink
            className="hover:text-gray-300 text-sm text-gray-500 prose"
            href={page?.data?.attributes?.path || externalUrl || ""}
            target={linkTarget?.data?.attributes?.value || ""}
          >
            {label}
          </TextLink>
        </div>
      )}
      <ul
        role="list"
        className={classNames("flex", {
          "gap-y-1": !configuration,
          "gap-y-0": configuration?.context === "DropdownNavbarItem",
          "flex-col":
            orientation ===
              Enum_Componentnavigationlinklist_Orientation.Vertical ||
            !!!orientation,
          [`flex-wrap md:text-center md:flex-row gap-x-${
            gap?.data?.attributes?.value || 6
          }`]:
            orientation ===
            Enum_Componentnavigationlinklist_Orientation.Horizontal,
          "justify-center":
            align === Enum_Componentnavigationlinklist_Align.Center,
        })}
      >
        {links &&
          links.filter(notEmpty).map((item) => (
            <li
              className={classNames({
                "flex items-center": true,
                "gap-2": item?.image?.data?.attributes?.url && item?.label,
                "inline-block":
                  orientation ===
                  Enum_Componentnavigationlinklist_Orientation.Horizontal,
              })}
              key={item?.id}
            >
              <Link
                href={
                  item?.externalUrl || item?.page?.data?.attributes?.path || "#"
                }
                target={item.linkTarget?.data?.attributes?.value || "_self"}
                className={classNames("flex items-center w-full", {
                  "hover:bg-gray-50 p-4 rounded-lg":
                    configuration?.context === "DropdownNavbarItem",
                })}
              >
                {item?.image?.data?.attributes?.url && (
                  <Image
                    src={item?.image?.data?.attributes?.url || ""}
                    alt={item?.image?.data?.attributes?.alternativeText || ""}
                    width={item?.image?.data?.attributes?.width || 512}
                    height={item?.image?.data?.attributes?.height || 512}
                    style={{
                      width: `${item?.imageWidth}px` || "auto",
                      height: `${item?.imageHeight}px` || "auto",
                    }}
                    className={classNames({
                      "object-contain":
                        item?.imageFit ===
                        Enum_Componentnavigationlink_Imagefit.Contain,
                      "object-cover":
                        item?.imageFit ===
                        Enum_Componentnavigationlink_Imagefit.Cover,
                    })}
                  />
                )}
                <div>
                  <Paragraph
                    paragraphClassName={classNames(`text-${textSizeClass}`, {
                      "font-bold":
                        configuration?.context === "DropdownNavbarItem" &&
                        item?.description,
                    })}
                  >
                    <span className="whitespace-nowrap">
                      {item?.label || ""}
                    </span>
                  </Paragraph>
                  {item?.description && (
                    <Paragraph>{item?.description}</Paragraph>
                  )}
                </div>
              </Link>
            </li>
          ))}
      </ul>
    </div>
  );
}
